import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState, useMemo } from "react";
import "./TransactionHistory.css";
import axios from "../../../../../Uri";

export default function TransactionHistory(props) {
  const [history, setHistory] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionId, setTransactionId] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check if user is admin (not manager) - only visible to admin
  const isAdmin = useMemo(() => {
    try {
      const storedData = sessionStorage.getItem("userdata");
      if (storedData) {
        const userData = JSON.parse(storedData);
        return userData?.data?.userType !== "manager";
      }
    } catch (err) {
      console.error("Error parsing userdata:", err);
    }
    return false;
  }, []);

  useEffect(() => {
    fetchData();
  }, [props.guestdetails.id]);

  async function fetchData() {
    try {
      const response = await axios.get(
        `/payment/getTrasactionHistoryByGuestId/${props.guestdetails.id}`
      );
      console.log(response.data);
      setHistory(response.data);
    } catch (err) {
      console.error("Error fetching transaction history:", err);
      setError("Failed to load transaction history");
    }
  }

  const handleUpdateClick = (row) => {
    setSelectedTransaction(row);
    setTransactionId(row.transactionId || "");
    setAmountPaid(row.amountPaid?.toString() || "");
    setError("");
    setSuccess("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
    setTransactionId("");
    setAmountPaid("");
    setError("");
    setSuccess("");
  };

  const handleUpdate = async () => {
    // Validation: at least one field must be provided
    if (!transactionId.trim() && !amountPaid.trim()) {
      setError("Please provide at least Transaction ID or Amount Paid");
      return;
    }

    // Validate amount if provided
    if (amountPaid.trim() && (isNaN(amountPaid) || parseFloat(amountPaid) < 0)) {
      setError("Amount Paid must be a valid positive number");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const updateRequest = {};
      if (transactionId.trim()) {
        updateRequest.transactionId = transactionId.trim();
      }
      if (amountPaid.trim()) {
        updateRequest.amountPaid = parseFloat(amountPaid);
      }

      await axios.put(
        `/payment/updateTransaction/${selectedTransaction.id}`,
        updateRequest,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess("Transaction updated successfully");
      
      // Refresh the data after a short delay
      setTimeout(() => {
        fetchData();
        handleCloseDialog();
      }, 1000);

    } catch (err) {
      console.error("Error updating transaction:", err);
      
      // Check for CORS errors
      if (err.message?.includes("CORS") || err.code === "ERR_NETWORK") {
        setError(
          "CORS Error: The server's CORS configuration may not be properly handling the PATCH method. " +
          "Please ensure the backend allows PATCH in Access-Control-Allow-Methods and handles OPTIONS preflight requests."
        );
      } else {
        setError(
          err.response?.data?.message || 
          "Failed to update transaction. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: "70vh", 
          overflow: "auto",
          position: "relative",
          "& .MuiTable-root": {
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%"
          },
          "& .MuiTableCell-head": {
            boxSizing: "border-box"
          },
          "& .MuiTableCell-body": {
            boxSizing: "border-box"
          }
        }}
      >
        <Table 
          sx={{ 
            minWidth: 650,
            tableLayout: "fixed",
            width: "100%"
          }} 
          aria-label="transaction history table" 
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell 
                align="left" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "10%" : "12%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                PAYMENT ID
              </TableCell>
              <TableCell 
                align="left" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "15%" : "18%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                TRANSACTION DATE
              </TableCell>
              {/* <TableCell 
                align="right" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "12%" : "12%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                REFUND
              </TableCell> */}
              <TableCell 
                align="right" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "12%" : "12%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                AMOUNT
              </TableCell>

              <TableCell 
                align="left" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "10%" : "12%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                BED ID
              </TableCell>
              <TableCell 
                align="left" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "18%" : "22%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                PAYMENT PURPOSE
              </TableCell>
              <TableCell 
                align="left" 
                sx={{ 
                  fontWeight: "bold",
                  width: isAdmin ? "23%" : "24%",
                  padding: "16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  boxSizing: "border-box"
                }}
              >
                TRANSACTION ID
              </TableCell>
              {isAdmin && (
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: "bold",
                    width: "10%",
                    padding: "16px",
                    borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    boxSizing: "border-box"
                  }}
                >
                  ACTION
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell 
                  align="left"
                  sx={{ 
                    width: isAdmin ? "10%" : "12%",
                    padding: "16px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.id}
                </TableCell>
                <TableCell 
                  align="left"
                  sx={{ 
                    width: isAdmin ? "15%" : "18%",
                    padding: "16px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.transactionDate || "-"}
                </TableCell>
                {/* <TableCell 
                  align="right"
                  sx={{ 
                    width: isAdmin ? "12%" : "12%",
                    padding: "16px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.refundAmount || "-"}
                </TableCell> */}
                <TableCell 
                  align="right"
                  sx={{ 
                    width: isAdmin ? "12%" : "12%",
                    padding: "16px",
                    whiteSpace: "nowrap",
                    fontWeight: "medium",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.amountPaid || "-"}
                </TableCell>
                <TableCell 
                  align="left"
                  sx={{ 
                    width: isAdmin ? "18%" : "22%",
                    padding: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {props.guestdetails.bedId || "-"}
                </TableCell>
                <TableCell 
                  align="left"
                  sx={{ 
                    width: isAdmin ? "18%" : "22%",
                    padding: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.paymentPurpose || "-"}
                </TableCell>
                <TableCell 
                  align="left"
                  sx={{ 
                    width: isAdmin ? "23%" : "24%",
                    padding: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box"
                  }}
                >
                  {row.transactionId || "-"}
                </TableCell>
                {isAdmin && (
                  <TableCell 
                    align="center"
                    sx={{ 
                      width: "10%",
                      padding: "16px",
                      whiteSpace: "nowrap",
                      boxSizing: "border-box"
                    }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleUpdateClick(row)}
                      aria-label="update transaction"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Update Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Transaction #{selectedTransaction?.id}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Transaction ID"
            type="text"
            fullWidth
            variant="outlined"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Amount Paid"
            type="number"
            fullWidth
            variant="outlined"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            inputProps={{ step: "0.01", min: "0" }}
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            At least one field must be provided to update the transaction.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

